import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdge } from "@/lib/invoke-edge";
import { useAuth } from "@/contexts/AuthContext";
import { useIsDevModeWithoutAuth } from "@/hooks/useDevModeData";
import { useUserOrganization } from "@/hooks/useUserOrganization";
import { useSessionContext } from "@/hooks/useSessionContext";
export { useIsAdminOrHR } from "@/hooks/useRoles";
import { mockEmployees } from "@/lib/mock-data";
import { toast } from "sonner";

export interface Employee {
  id: string;
  user_id: string;
  organization_id: string | null;
  employee_id: string | null;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
  job_title: string | null;
  department: string | null;
  status: "active" | "on_leave" | "inactive" | "exited";
  join_date: string | null;
  date_of_joining: string | null;
  phone: string | null;
  location: string | null;
  manager_id: string | null;
  created_at: string;
  updated_at: string;
  // Extended fields from employee_details (null when not yet filled in)
  gender: string | null;
  date_of_birth: string | null;
  blood_group: string | null;
  marital_status: string | null;
  nationality: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  country: string | null;
  emergency_contact_name: string | null;
  emergency_contact_relation: string | null;
  emergency_contact_phone: string | null;
  bank_name: string | null;
  bank_account_number: string | null;
  bank_ifsc: string | null;
  bank_branch: string | null;
  employee_id_number: string | null;
  pan_number: string | null;
  aadhaar_last_four: string | null;
  uan_number: string | null;
  esi_number: string | null;
}

export interface CreateEmployeeData {
  full_name: string;
  email: string;
  job_title?: string;
  department?: string;
  status?: Employee["status"];
  join_date?: string;
  phone?: string;
  location?: string;
  manager_id?: string | null;
}

export interface UpdateEmployeeData extends Partial<CreateEmployeeData> {
  id: string;
}

// Check if user has admin, HR, finance, or payroll role for read access
export function useIsAdminHROrFinance() {
  const { data: session, isLoading } = useSessionContext();
  const data = session ?? { isSuperAdmin: false, organizationId: null, roles: [] };
  return {
    data: data.isSuperAdmin
      ? true
      : data.organizationId
        ? data.roles.some((r) => ["admin", "hr", "finance", "payroll"].includes(r))
        : false,
    isLoading,
  };
}

// Fetch all employees (profiles) - ORGANIZATION-SCOPED to prevent cross-tenant data bleed
export function useEmployees() {
  const { user } = useAuth();
  const isDevMode = useIsDevModeWithoutAuth();
  const { data: orgData } = useUserOrganization();
  const orgId = orgData?.organizationId;

  return useQuery({
    // NOTE: queryKey intentionally omits `hasAccess` — RLS is the source of
    // truth, and including a transient role-loading flag here used to cause
    // a degraded empty-array snapshot to be cached against a different key
    // than the eventual authoritative result, leaving the directory blank
    // while the KPI (which doesn't depend on the role hook) showed 49.
    queryKey: ["employees", user?.id, isDevMode, orgId],
    queryFn: async () => {
      // eslint-disable-next-line no-console
      console.log("[useEmployees] start", { orgId, hasUser: !!user, isDevMode });
      const t0 = performance.now();
      if (isDevMode) return mockEmployees;
      if (!user) return [];
      // HARD GUARD: Never query profiles without org scope — prevents cross-tenant data bleed
      if (!orgId) return [];

      // Always attempt the full profiles select first — RLS will filter rows
      // for non-admin viewers. Fall back to profiles_safe only if the row
      // policy denies the read entirely (error path), never on empty results.
      const { data, error } = await supabase
        .from("profiles")
        .select("id,user_id,organization_id,employee_id,full_name,email,avatar_url,job_title,department,status,join_date,phone,location,manager_id,created_at,updated_at")
        .eq("organization_id", orgId)
        .order("full_name", { ascending: true })
        .limit(500);
      if (!error && data) {
        // eslint-disable-next-line no-console
        console.log("[useEmployees] profiles loaded", { ms: Math.round(performance.now() - t0), count: data.length });
        const employees = data as unknown as Employee[];

        // Fix stale on_leave statuses: check if employee actually has an approved leave covering today
        const today = new Date().toISOString().split("T")[0];
        const onLeaveIds = employees.filter(e => e.status === "on_leave").map(e => e.id);
        
        if (onLeaveIds.length > 0) {
          const { data: activeLeaves } = await supabase
            .from("leave_requests")
            .select("user_id")
            .eq("status", "approved")
            .eq("organization_id", orgId)
            .lte("from_date", today)
            .gte("to_date", today);

          const usersOnLeaveToday = new Set((activeLeaves || []).map(l => l.user_id));

          // Reset profiles that show on_leave but have no active leave today
          const staleProfiles = employees.filter(
            e => e.status === "on_leave" && !usersOnLeaveToday.has(e.user_id)
          );

          if (staleProfiles.length > 0) {
            // Update in background, don't block the UI
            Promise.all(
              staleProfiles.map(p =>
                supabase.from("profiles").update({ status: "active" }).eq("id", p.id)
              )
            ).catch(err => console.warn("Failed to reset stale on_leave status:", err));

            // Also fix the returned data immediately
            for (const emp of employees) {
              if (emp.status === "on_leave" && !usersOnLeaveToday.has(emp.user_id)) {
                emp.status = "active";
              }
            }
          }
        }

        // eslint-disable-next-line no-console
        console.log("[useEmployees] done", { ms: Math.round(performance.now() - t0), count: employees.length });
        return employees;
      }

      // RLS denied the full row — fall back to the public-safe view so the
      // directory still renders for non-admin/HR/Finance viewers.
      const { data: safeData, error: safeError } = await supabase
        .from("profiles_safe" as any)
        .select("*")
        .order("full_name", { ascending: true })
        .limit(500);
      if (safeError) throw safeError;
      return (safeData as any[]).map((d) => ({
        ...d,
        organization_id: orgId,
        email: null,
        phone: null,
      })) as Employee[];
    },
    enabled: (!!user && !!orgId) || isDevMode,
    staleTime: 5 * 60_000, // 5 min — employee roster rarely changes mid-session
    refetchOnWindowFocus: false,
  });
}

/**
 * Lightweight stats hook for the Dashboard.
 * Avoids pulling the full profiles row payload (used to be ~50KB per Dashboard mount)
 * by selecting only `id, user_id, status` instead of `select('*')`.
 */
export function useEmployeeStats() {
  const { user } = useAuth();
  const { data: orgData } = useUserOrganization();
  const statsOrgId = orgData?.organizationId;

  const { data: employees = [] } = useQuery({
    queryKey: ["employee-stats-lite", statsOrgId],
    queryFn: async () => {
      if (!statsOrgId) return [];
      const { data, error } = await supabase
        .from("profiles")
        .select("id, user_id, status")
        .eq("organization_id", statsOrgId)
        .limit(1000);
      if (error) throw error;
      return (data || []) as Array<{ id: string; user_id: string | null; status: string | null }>;
    },
    enabled: !!user && !!statsOrgId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const { data: approvedLeavesToday = [] } = useQuery({
    queryKey: ["employee-stats-leaves-today", statsOrgId],
    queryFn: async () => {
      if (!statsOrgId) return [];
      const today = new Date().toISOString().split("T")[0];
      const { data } = await supabase
        .from("leave_requests")
        .select("user_id, profile_id")
        .eq("status", "approved")
        .eq("organization_id", statsOrgId)
        .lte("from_date", today)
        .gte("to_date", today);
      return data || [];
    },
    enabled: !!user && !!statsOrgId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const leaveProfileIds = new Set(
    approvedLeavesToday
      .map((l) => l.profile_id)
      .filter(Boolean)
  );
  const leaveUserIds = new Set(
    approvedLeavesToday
      .map((l) => l.user_id)
      .filter(Boolean)
  );

  const onLeaveCount = employees.filter(
    (e) => leaveProfileIds.has(e.id) || leaveUserIds.has(e.user_id)
  ).length;

  const stats = {
    total: employees.length,
    active: employees.filter((e) => e.status !== "inactive" && e.status !== "exited").length - onLeaveCount,
    onLeave: onLeaveCount,
    inactive: employees.filter((e) => e.status === "inactive").length,
    onLeaveIds: { profileIds: leaveProfileIds, userIds: leaveUserIds },
  };

  return stats;
}

// Create employee — always via edge function so a real auth account is created
// and the user appears in both Employees and Settings
export function useCreateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateEmployeeData) => {
      const { data: result, error } = await supabase.functions.invoke("manage-roles", {
        body: { action: "create_user", ...data, role: "employee" },
      });

      if (error || result?.error) throw new Error(result?.error || error?.message || "Failed to create employee");
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      queryClient.invalidateQueries({ queryKey: ["attendance"] });
      queryClient.invalidateQueries({ queryKey: ["attendance-stats"] });
      toast.success("Employee added. They can sign in with their email address.");
    },
    onError: (error: any) => {
      const msg = String(error?.message ?? "");
      if (msg.includes("CIRCULAR_MANAGER")) {
        toast.error("Cannot set this manager — it would create a circular reporting chain.");
        return;
      }
      toast.error(msg);
    },
  });
}

// Update employee
//
// Bidirectional MS365 sync: fields that exist in Microsoft Graph
// (department / job_title / phone / location / manager_id) are routed
// through the `ms365-sync` Edge function so PG + Graph stay in lock-step
// (Graph PATCH first; PG only updates if Graph returned 2xx). Other fields
// (full_name, email, status, join_date) stay on the direct profiles UPDATE
// path because Graph either owns them differently (displayName/UPN) or
// doesn't model them at all.
const GRAPH_SYNCED_KEYS = ["department", "job_title", "phone", "location"] as const;
type GraphSyncedKey = (typeof GRAPH_SYNCED_KEYS)[number];

export function useUpdateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateEmployeeData) => {
      // Resolve org_id to enforce tenant isolation on update
      const { data: profile } = await supabase
        .from("profiles")
        .select("organization_id")
        .eq("id", id)
        .single();

      const { data: callerProfile } = await supabase
        .from("profiles")
        .select("organization_id")
        .eq("user_id", (await supabase.auth.getUser()).data.user?.id ?? "")
        .maybeSingle();

      if (!profile || !callerProfile || profile.organization_id !== callerProfile.organization_id) {
        throw new Error("Cannot update employee from another organization.");
      }

      // Split the payload — anything Graph-mirrored goes through the
      // ms365-sync handler so PG + MS365 stay consistent. The remaining
      // PG-only fields land on the direct profiles UPDATE below.
      const graphAttrs: Partial<Record<GraphSyncedKey, string | null>> = {};
      const localOnly: Record<string, unknown> = { ...data };
      for (const k of GRAPH_SYNCED_KEYS) {
        if (k in localOnly) {
          graphAttrs[k] = (localOnly[k] as string | null | undefined) ?? null;
          delete localOnly[k];
        }
      }
      const managerProvided = "manager_id" in localOnly;
      const managerValue = managerProvided ? (localOnly.manager_id as string | null) : undefined;
      if (managerProvided) delete localOnly.manager_id;

      // 1. Graph-mirrored attributes (dept/title/phone/location)
      if (Object.keys(graphAttrs).length > 0) {
        const { data: attrRes, error: attrErr } = await invokeEdge("ms365-sync", {
          body: {
            action: "update_user_attributes",
            profile_id: id,
            attributes: graphAttrs,
          },
        });
        if (attrErr || attrRes?.error) {
          throw new Error(attrRes?.error || attrErr?.message || "Failed to sync attributes to MS365");
        }
      }

      // 2. Manager assignment (separate Graph endpoint)
      if (managerProvided) {
        const { data: mgrRes, error: mgrErr } = await invokeEdge("ms365-sync", {
          body: {
            action: "set_manager",
            profile_id: id,
            manager_profile_id: managerValue || null,
          },
        });
        if (mgrErr || mgrRes?.error) {
          throw new Error(mgrRes?.error || mgrErr?.message || "Failed to update manager");
        }
      }

      // 3. PG-only fields via direct profiles UPDATE. profiles has no real
      //    organization_id column on this schema (it's synthesized on SELECT
      //    by the v2 adapter via organization_members); .eq("organization_id",…)
      //    in a WHERE/SET ends up on the Sequelize model which doesn't
      //    declare it and bombs with "column \"Profile.organization_id\" does
      //    not exist". The cross-org check above already guarded against this
      //    id belonging to another tenant. Skip the round-trip if nothing's
      //    left to write.
      // full_name is a generated column — translate into first/last/display.
      if (typeof localOnly.full_name === "string" && localOnly.full_name) {
        const trimmed = (localOnly.full_name as string).trim();
        const spaceIdx = trimmed.indexOf(" ");
        localOnly.first_name = spaceIdx > 0 ? trimmed.slice(0, spaceIdx) : trimmed;
        localOnly.last_name = spaceIdx > 0 ? trimmed.slice(spaceIdx + 1) : "";
        localOnly.display_name = trimmed;
        delete localOnly.full_name;
      }
      if (Object.keys(localOnly).length === 0) {
        const { data: refreshed } = await supabase
          .from("profiles")
          .select()
          .eq("id", id)
          .single();
        return refreshed;
      }

      const { data: employee, error } = await supabase
        .from("profiles")
        .update(localOnly)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return employee;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      queryClient.invalidateQueries({ queryKey: ["attendance"] });
      queryClient.invalidateQueries({ queryKey: ["attendance-stats"] });
      queryClient.invalidateQueries({ queryKey: ["user-roles"] });
      queryClient.invalidateQueries({ queryKey: ["org-chart-profiles"] });
    },
    onError: (error: any) => {
      const msg = String(error?.message ?? "");
      if (msg.includes("CIRCULAR_MANAGER")) {
        toast.error("Cannot set this manager — it would create a circular reporting chain.");
        return;
      }
      toast.error(msg);
    },
  });
}

// Delete employee — removes profile + auth account via edge function
export function useDeleteEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Resolve caller org for tenant isolation
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data: callerProfile } = await supabase.from("profiles").select("organization_id").eq("user_id", user.id).maybeSingle();
      if (!callerProfile?.organization_id) throw new Error("Organization not found");

      // Get the user_id from the profile (org-scoped)
      const { data: profile, error: profileErr } = await supabase
        .from("profiles")
        .select("user_id")
        .eq("id", id)
        .eq("organization_id", callerProfile.organization_id)
        .single();

      if (profileErr) throw profileErr;
      if (!profile) throw new Error("Employee not found in your organization.");

      // Delete via edge function (handles auth account + roles cleanup)
      await supabase.functions.invoke("manage-roles", {
        body: { action: "delete_user", user_id: profile.user_id },
      });

      // Always explicitly delete the profile row to ensure it is removed from the UI.
      // The edge function may only revoke auth/roles and not delete the profile record.
      const { error: delErr } = await supabase
        .from("profiles")
        .delete()
        .eq("id", id)
        .eq("organization_id", callerProfile.organization_id);
      if (delErr) throw delErr;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      queryClient.invalidateQueries({ queryKey: ["attendance"] });
      queryClient.invalidateQueries({ queryKey: ["attendance-stats"] });
      toast.success("Employee deleted. Their profile and login access have been permanently removed.");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}
